import { useEffect, useRef, useState } from "react";
import { useMsal } from "@azure/msal-react";
import { useTranslation } from "react-i18next";
import { getSstWebSocketUrl, type SttClientFrame, type SttServerFrame } from "../../api";
import { useLogin, getToken } from "../../authConfig";
import { ActionIcon, Tooltip } from "@mantine/core";
import { MdMic } from "react-icons/md";
import { useUserSettings } from "../../UserSettingsContext";

export interface SpeechStateChange {
    text: string;
    state: "start" | "stop";
    sstRequestId?: string;
}

export interface SpeechToTextProps {
    onSpeechToText: (state: SpeechStateChange) => void;
}

const RESULT_TIMEOUT_MS = 30000;

// Speech-to-text via the /v2/sst/wss WebSocket: PCM16 audio is streamed while
// recording; on stop the client sends stt.audio.done and waits for the final
// stt.result frame.
export const SpeechToText = ({ onSpeechToText }: SpeechToTextProps) => {
    const { t } = useTranslation();
    const [isRecording, setIsRecording] = useState(false);
    const [isProcessing, setIsProcessing] = useState(false);
    const wsRef = useRef<WebSocket | null>(null);
    const wsReadyRef = useRef(false);
    const stopRequestedRef = useRef(false);
    const finishedRef = useRef(false);
    const pendingChunksRef = useRef<Int16Array<ArrayBuffer>[]>([]);
    const resultTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const processorRef = useRef<AudioWorkletNode | null>(null);
    const audioContextRef = useRef<AudioContext | null>(null);
    const sourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
    const mediaStreamRef = useRef<MediaStream | null>(null);
    const { userSettings, loading, error } = useUserSettings();
    const client = useMsal().instance;

    const stopAudioPipeline = async () => {
        processorRef.current?.port.close();
        processorRef.current?.disconnect();
        sourceRef.current?.disconnect();
        mediaStreamRef.current?.getTracks().forEach(track => track.stop());
        if (audioContextRef.current && audioContextRef.current.state !== "closed") {
            await audioContextRef.current.close();
        }
        processorRef.current = null;
        sourceRef.current = null;
        mediaStreamRef.current = null;
        audioContextRef.current = null;
    };

    useEffect(() => {
        return () => {
            void stopAudioPipeline();
            wsRef.current?.close();
            if (resultTimerRef.current) clearTimeout(resultTimerRef.current);
        };
    }, []);

    if (!useLogin || loading || error || !userSettings) return null;

    const finish = (text: string, sstRequestId?: string) => {
        if (finishedRef.current) return;
        finishedRef.current = true;
        if (resultTimerRef.current) {
            clearTimeout(resultTimerRef.current);
            resultTimerRef.current = null;
        }
        void stopAudioPipeline();
        onSpeechToText({ state: "stop", text, sstRequestId });
        setIsRecording(false);
        setIsProcessing(false);
    };

    const sendAudioDone = (ws: WebSocket) => {
        for (const chunk of pendingChunksRef.current) {
            ws.send(chunk);
        }
        pendingChunksRef.current = [];
        ws.send(JSON.stringify({ type: "stt.audio.done" } satisfies SttClientFrame));
        // Safety net: don't leave the mic button stuck if no result ever arrives.
        resultTimerRef.current = setTimeout(() => ws.close(), RESULT_TIMEOUT_MS);
    };

    const startRecording = async () => {
        if (isProcessing || isRecording) return;
        onSpeechToText({ state: "start", text: "" });
        try {
            if (!client) throw new Error("Client is not initialized");
            const idToken = await getToken(client);
            if (!idToken) throw new Error("No authentication token available");

            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            // Run at the device's native rate — forcing a low rate (e.g. 16 kHz)
            // makes the browser resample the mic input, a known source of audio
            // artifacts. The actual rate is declared via ?sampleRate= and the
            // server-side pipeline resamples cleanly.
            const audioContext = new AudioContext();
            // The context is created after async awaits, i.e. possibly outside the
            // user-activation window — some browsers then start it suspended.
            if (audioContext.state === "suspended") {
                await audioContext.resume();
            }
            await audioContext.audioWorklet.addModule("/static/audio-worklet.js");
            const source = audioContext.createMediaStreamSource(stream);
            const processor = new AudioWorkletNode(audioContext, "pcm-processor");

            wsReadyRef.current = false;
            stopRequestedRef.current = false;
            finishedRef.current = false;
            pendingChunksRef.current = [];

            const ws = new WebSocket(getSstWebSocketUrl(idToken, audioContext.sampleRate));
            ws.binaryType = "arraybuffer";

            processor.port.onmessage = event => {
                const chunk = new Int16Array(event.data as ArrayBuffer);
                if (wsReadyRef.current && ws.readyState === WebSocket.OPEN) {
                    ws.send(chunk);
                } else {
                    // Buffer audio captured before the server confirmed with stt.ready
                    pendingChunksRef.current.push(chunk);
                }
            };

            ws.onmessage = e => {
                let frame: SttServerFrame;
                try {
                    frame = JSON.parse(e.data);
                } catch {
                    return;
                }
                switch (frame.type) {
                    case "stt.ready":
                        wsReadyRef.current = true;
                        if (stopRequestedRef.current) {
                            sendAudioDone(ws);
                        } else {
                            for (const chunk of pendingChunksRef.current) {
                                ws.send(chunk);
                            }
                            pendingChunksRef.current = [];
                        }
                        break;
                    case "stt.result":
                        finish(frame.text, frame.sstRequestId);
                        break;
                    case "stt.error":
                        console.error("STT error:", frame.error);
                        finish("");
                        break;
                    default: {
                        // A new server frame fails the build here until it is handled.
                        const unhandled: never = frame;
                        console.warn("Unhandled STT frame:", unhandled);
                    }
                }
            };

            ws.onerror = event => console.error("STT websocket error:", event);
            ws.onclose = () => {
                wsRef.current = null;
                finish("");
            };

            source.connect(processor).connect(audioContext.destination);

            wsRef.current = ws;
            audioContextRef.current = audioContext;
            sourceRef.current = source;
            processorRef.current = processor;
            mediaStreamRef.current = stream;
            setIsRecording(true);
        } catch (err) {
            console.error("STT start error:", err);
            await stopAudioPipeline();
            wsRef.current?.close();
            finish("");
        }
    };

    const stopRecording = async () => {
        if (!isRecording) return;
        setIsRecording(false);
        setIsProcessing(true);

        // Keep capturing a short tail so the end of the last word isn't clipped,
        // then have the worklet flush its partial batch and let the port drain
        // before tearing the pipeline down.
        await new Promise(resolve => setTimeout(resolve, 200));
        processorRef.current?.port.postMessage("flush");
        await new Promise(resolve => setTimeout(resolve, 50));
        await stopAudioPipeline();

        const ws = wsRef.current;
        if (!ws || ws.readyState !== WebSocket.OPEN) {
            finish("");
            return;
        }
        if (wsReadyRef.current) {
            sendAudioDone(ws);
        } else {
            // stt.ready hasn't arrived yet — flush and send stt.audio.done once it does
            stopRequestedRef.current = true;
        }
    };

    const tooltipLabel = isRecording ? t("tooltips.stopRecording") : t("tooltips.askWithVoice");
    const iconColor = isRecording ? "red.8" : "dark.6";
    const handleClick = isRecording ? stopRecording : startRecording;

    return (
        <Tooltip label={tooltipLabel}>
            <ActionIcon variant="transparent" color={iconColor} onClick={handleClick} disabled={isProcessing} aria-label={tooltipLabel}>
                <MdMic size={24} />
            </ActionIcon>
        </Tooltip>
    );
};
